import {
    formatDateWithWeekday,
    getISODate,
    parseDate,
    secondsToHours,
    sumNetWorkSeconds,
} from '@/lib/dates'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

export interface DayGroup {
    dateKey: string
    dateLabel: string
    entries: TimeEntriesExpanded[]
    totalWorkHours: number
    entryIds: string[]
    hasOverlap: boolean
    overlappingEntryIds: Map<string, number>
}

export function groupEntriesByDate(entries: TimeEntriesExpanded[]): DayGroup[] {
    const grouped = new Map<string, TimeEntriesExpanded[]>()

    for (const entry of entries) {
        const dateKey = getISODate(entry.clock_in)
        const existing = grouped.get(dateKey)
        if (existing) {
            existing.push(entry)
        } else {
            grouped.set(dateKey, [entry])
        }
    }

    const dayGroups: DayGroup[] = []

    for (const [dateKey, dayEntries] of grouped) {
        dayEntries.sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime())

        const totalWorkHours = sumWorkHours(dayEntries)

        const overlappingEntryIds = detectOverlaps(dayEntries)

        dayGroups.push({
            dateKey,
            dateLabel: formatDateWithWeekday(dateKey),
            entries: dayEntries,
            totalWorkHours,
            entryIds: dayEntries.map(e => e.id),
            hasOverlap: overlappingEntryIds.size > 0,
            overlappingEntryIds,
        })
    }

    dayGroups.sort((a, b) => b.dateKey.localeCompare(a.dateKey))

    return dayGroups
}

export function detectOverlaps(entries: TimeEntriesExpanded[]): Map<string, number> {
    const overlapping = new Map<string, number>()
    const nonBreakEntries = entries.filter(e => e.entry_type !== ('break' as string))

    for (let i = 1; i < nonBreakEntries.length; i++) {
        const prev = nonBreakEntries[i - 1]
        const curr = nonBreakEntries[i]

        if (!prev.clock_out) continue

        const prevEnd = parseDate(prev.clock_out).getTime()
        const currStart = parseDate(curr.clock_in).getTime()

        const overlapMs = prevEnd - currStart
        if (overlapMs > 60000) {
            const overlapMinutes = Math.ceil(overlapMs / 60000)
            overlapping.set(curr.id, overlapMinutes)
        }
    }

    return overlapping
}

export function sumWorkHours(entries: TimeEntriesExpanded[]): number {
    return secondsToHours(sumNetWorkSeconds(entries))
}
